import { ArrowLeftIcon, CalendarClockIcon, ChevronRightIcon, DownloadIcon, LanguagesIcon, SendIcon, Trash2Icon, ZapIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DELIVERY_SEGMENTS, DeliveryBar, DeliverySwatch } from '@/components/DeliveryBar';
import { ErrorState } from '@/components/ErrorState';
import { TableSkeleton } from '@/components/LoadingBlock';
import { Pagination } from '@/components/Pagination';
import { MessageStatusBadge, RecipientStatusBadge } from '@/components/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TokenText } from '@/features/templates/TokenText';
import { useJobContext } from '@/layouts/JobLayout';
import { api, errorMessage, isApiError } from '@/lib/api';
import { useMe } from '@/lib/auth';
import { fmtDateTime, fmtDateTimeInZone, fmtNumber, fmtPercent, tzAbbreviation } from '@/lib/format';
import type { MessageCounts, RecipientStatus } from '@/lib/types';
import { cn } from '@/lib/utils';
import { audienceLabel, senderLabel } from './HistoryPage';
import { useCancelMessage, useMessage, useRecipients, useSendNow } from './queries';
import { providerLabel } from './RecipientPage';

type Filter = 'all' | RecipientStatus;

const FILTERS: Array<{ value: Filter; label: string; count?: (c: MessageCounts) => number }> = [
  { value: 'all', label: 'All' },
  { value: 'delivered', label: 'Delivered', count: (c) => c.delivered },
  { value: 'sent', label: 'Sent', count: (c) => c.sent },
  { value: 'failed', label: 'Failed', count: (c) => c.failed },
  { value: 'undelivered', label: 'Undelivered' },
  { value: 'queued', label: 'Queued', count: (c) => c.queued + c.sending },
  { value: 'skipped_opted_out', label: 'Opted out', count: (c) => c.optedOut },
  { value: 'skipped_invalid', label: 'Invalid', count: (c) => c.invalid },
];

/**
 * One message: the text as the crew saw it (in each language it went out in), the facts behind the send, how
 * delivery broke down, and every recipient with their own outcome. Live while the send is in flight.
 */
export function MessageDetailPage() {
  const { job, permissions } = useJobContext();
  const me = useMe();
  const navigate = useNavigate();
  const { messageId = '' } = useParams();
  const message = useMessage(job.id, messageId);
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [variant, setVariant] = useState<string>('source');
  const sendNow = useSendNow(job.id);
  const cancel = useCancelMessage(job.id);
  const [confirm, setConfirm] = useState<'send' | 'send-override' | 'cancel' | null>(null);
  const m = message.data?.message;
  const live = !!m && (m.status === 'queued' || m.status === 'sending');
  const recipients = useRecipients(job.id, messageId, { page, pageSize, status: filter === 'all' ? undefined : filter }, live);
  const tz = job.timezone ?? me.tenant.quietHours.timezone;

  if (message.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-48 w-full" />
        <TableSkeleton />
      </div>
    );
  }
  if (message.isError || !m) return <ErrorState error={message.error ?? new Error('Message not found')} onRetry={() => void message.refetch()} />;

  const c = m.counts ?? { queued: 0, sending: 0, sent: 0, delivered: 0, failed: 0, optedOut: 0, invalid: 0 };
  const attempted = c.sent + c.delivered + c.failed + c.queued + c.sending;
  const upcoming = m.status === 'draft' || m.status === 'scheduled';
  const translations = Object.entries(m.translations ?? {});
  const shownText = variant === 'source' ? m.renderedBody : (m.translations[variant]?.rendered ?? m.renderedBody);
  const reached = c.delivered + c.failed > 0 ? (c.delivered / (c.delivered + c.failed)) * 100 : null;
  // The gateway that carried this send; the account id comes from any settled recipient on the current page.
  const carrier = m.provider ? { provider: m.provider, providerAccountSid: recipients.data?.items.find((x) => x.providerAccountSid)?.providerAccountSid ?? null } : undefined;
  const openReceipt = (recipientId: string) => navigate(`/jobs/${job.id}/history/${m.id}/recipients/${recipientId}`);

  const doSend = async (bypass: boolean) => {
    try {
      const res = await sendNow.mutateAsync({ messageId: m.id, bypassQuietHours: bypass });
      toast.success(`Sending to ${res.recipients.eligible.toLocaleString()} recipients`);
      setConfirm(null);
    } catch (err) {
      if (isApiError(err) && err.code === 'quiet_hours') {
        setConfirm('send-override');
        toast.error(err.message);
        return;
      }
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={`/jobs/${job.id}/history${upcoming ? '?view=upcoming' : ''}`}>
            <ArrowLeftIcon /> {upcoming ? 'Upcoming' : 'History'}
          </Link>
        </Button>
        <div className="flex flex-wrap gap-2">
          {upcoming && permissions.send && (
            <>
              <Button variant="outline" size="sm" onClick={() => setConfirm('cancel')}>
                <Trash2Icon /> {m.status === 'scheduled' ? 'Cancel send' : 'Discard draft'}
              </Button>
              <Button size="sm" onClick={() => setConfirm('send')}>
                <SendIcon /> Send now
              </Button>
            </>
          )}
          {!upcoming && (
            <Button variant="outline" size="sm" onClick={() => void api.messages.exportCsv(job.id, m.id).catch((e: unknown) => toast.error(errorMessage(e)))}>
              <DownloadIcon /> Export recipients
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-5 sm:gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start">
        {/* ------------------------------------------------------------ the message */}
        <section className="bg-card border" aria-label="Message">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3">
            <h2 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Message</h2>
            <div className="flex items-center gap-2">
              {live && (
                <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  <span className="bg-primary size-1.5 animate-pulse rounded-full" aria-hidden /> updating live
                </span>
              )}
              <MessageStatusBadge status={m.status} />
            </div>
          </div>
          <div className="px-5">
            {translations.length > 0 && (
              <div className="mb-2 flex flex-wrap items-center gap-1" role="tablist" aria-label="Language">
                <LanguagesIcon className="text-muted-foreground mr-1 size-3.5" aria-hidden />
                <LangPill on={variant === 'source'} onClick={() => setVariant('source')}>
                  {m.sourceLanguage.toUpperCase()} original
                </LangPill>
                {translations.map(([lang, t]) => (
                  <LangPill key={lang} on={variant === lang} onClick={() => setVariant(lang)} title={t.overridden ? 'Edited by the sender' : `Machine translation (${t.provider ?? 'auto'})`}>
                    {lang.toUpperCase()}
                    {t.overridden ? ' ✎' : ''}
                  </LangPill>
                ))}
              </div>
            )}
            <div className="bg-muted/40 border p-4 text-[15px] leading-relaxed">
              <TokenText body={shownText} />
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 px-5 py-5 sm:grid-cols-3">
            {m.status === 'scheduled' && m.scheduledAt && (
              <Fact label="Sends at" className="col-span-2 sm:col-span-3">
                <span className="flex items-center gap-1.5">
                  <CalendarClockIcon className="text-muted-foreground size-3.5" aria-hidden /> {fmtDateTimeInZone(m.scheduledAt, tz)} <span className="text-muted-foreground text-xs">site time · {tzAbbreviation(tz)}</span>
                </span>
              </Fact>
            )}
            <Fact label={upcoming ? 'Created' : 'Sent'}>{fmtDateTime(m.createdAt)}</Fact>
            <Fact label="By">
              <span className="flex items-center gap-1.5">
                {m.sendSource === 'automation' && <ZapIcon className="size-3.5" aria-hidden />} {senderLabel(m)}
                {m.automationRuleId && (
                  <Link to={`/jobs/${job.id}/automation/${m.automationRuleId}`} className="text-muted-foreground text-xs underline-offset-4 hover:underline">
                    rule
                  </Link>
                )}
              </span>
            </Fact>
            <Fact label="From">{m.fromNumber ?? '—'}</Fact>
            {!upcoming && (
              <Fact label="Sent via">
                {carrier ? (
                  <>
                    {providerLabel(carrier.provider)}
                    {carrier.providerAccountSid && (
                      <span className="text-muted-foreground block truncate font-mono text-xs" title={carrier.providerAccountSid}>
                        {carrier.providerAccountSid}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Fact>
            )}
            <Fact label="Audience">{audienceLabel(m)}</Fact>
            <Fact label="Segments">
              {m.segmentsPerMessage} per recipient <span className="text-muted-foreground text-xs">· {m.encoding}</span>
            </Fact>
            {translations.length > 0 && <Fact label="Translated to">{translations.map(([l]) => l.toUpperCase()).join(', ')}</Fact>}
            {m.completedAt && <Fact label="Completed">{fmtDateTime(m.completedAt)}</Fact>}
            {m.cancelledAt && <Fact label="Cancelled">{fmtDateTime(m.cancelledAt)}</Fact>}
          </dl>
        </section>

        {/* ------------------------------------------------------------ delivery */}
        <section className="bg-card border" aria-label="Delivery">
          <div className="flex flex-wrap items-baseline justify-between gap-3 px-5 pt-4 pb-3">
            <h2 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Delivery</h2>
            {!upcoming && reached !== null && (
              <span className="text-sm tabular-nums">
                <span className="font-display text-2xl leading-none tracking-tight">{fmtPercent(reached)}</span> <span className="text-muted-foreground text-xs">delivered of those attempted</span>
              </span>
            )}
          </div>
          {upcoming ? (
            <p className="text-muted-foreground px-5 pb-5 text-sm leading-relaxed">Recipients are resolved when the message goes out: everyone matching the audience at that moment, minus anyone who has opted out. Texts leave from {m.fromNumber ?? 'the job number'}.</p>
          ) : (
            <div className="px-5 pb-5">
              <DeliveryBar counts={c} total={m.recipientCount} size="md" />
              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
                <Stat segment="delivered" label="Delivered" value={c.delivered} total={m.recipientCount} />
                <Stat segment="sent" label="Sent, awaiting receipt" value={c.sent} total={m.recipientCount} />
                <Stat segment="failed" label="Failed or undelivered" value={c.failed} total={m.recipientCount} bad />
                <Stat segment="pending" label="Pending" value={c.queued + c.sending} total={m.recipientCount} />
                <Stat segment="skipped" label="Skipped: opted out" value={c.optedOut} total={m.recipientCount} />
                <Stat segment="skipped" label="Skipped: invalid number" value={c.invalid} total={m.recipientCount} />
              </dl>
              <p className="text-muted-foreground mt-4 text-xs leading-relaxed">
                {fmtNumber(attempted)} of {fmtNumber(m.recipientCount)} recipients were attempted. Delivery receipts come from the carrier and can take a few minutes.
              </p>
            </div>
          )}
        </section>
      </div>

      {/* ------------------------------------------------------------ recipients */}
      {!upcoming && (
        <section className="space-y-3" aria-label="Recipients">
          <Tabs
            value={filter}
            onValueChange={(v) => {
              setFilter(v as Filter);
              setPage(1);
            }}
          >
            <TabsList className="h-auto flex-wrap">
              {FILTERS.map((f) => (
                <TabsTrigger key={f.value} value={f.value} className="px-3">
                  {f.label}
                  {f.count && <span className="ml-1 text-xs tabular-nums opacity-70">{f.count(c)}</span>}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {recipients.isPending ? (
            <TableSkeleton rows={6} cols={5} />
          ) : recipients.isError ? (
            <ErrorState error={recipients.error} onRetry={() => void recipients.refetch()} />
          ) : (
            <>
              <p className="text-muted-foreground -mt-1 text-xs">Each row is a receipt: open it for the exact text that person was sent and the carrier's record.</p>
              <div className="bg-card overflow-x-auto border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipient</TableHead>
                      <TableHead className="hidden md:table-cell">Language</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Sent</TableHead>
                      <TableHead className="hidden sm:table-cell">Delivered / failed</TableHead>
                      <TableHead className="hidden xl:table-cell">Error</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipients.data.items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                          No recipients in this category.
                        </TableCell>
                      </TableRow>
                    )}
                    {recipients.data.items.map((r) => (
                      <TableRow
                        key={r.id}
                        className="cursor-pointer"
                        onClick={() => openReceipt(r.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') openReceipt(r.id);
                        }}
                        tabIndex={0}
                        aria-label={`Open the receipt for ${r.contactName ?? r.phoneDisplay}`}
                      >
                        <TableCell>
                          <div className="font-medium">
                            <Link to={`/jobs/${job.id}/history/${m.id}/recipients/${r.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                              {r.contactName ?? <span className="text-muted-foreground">Unknown</span>}
                            </Link>
                          </div>
                          <div className="text-muted-foreground text-xs tabular-nums">{r.phoneDisplay}</div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {r.language ? (
                            // A person's own text is stored when it differs from the message (translated or personalized); only a different language is a translation.
                            <Badge variant={r.language !== m.sourceLanguage ? 'secondary' : 'outline'} title={r.language !== m.sourceLanguage ? 'Sent in this person’s language' : r.renderedBody ? 'Sent in the original language, personalized for this person' : 'Sent in the original language'}>
                              {r.language.toUpperCase()}
                              {r.language !== m.sourceLanguage ? ' · translated' : ''}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <RecipientStatusBadge status={r.status} />
                          {r.errorCode && <div className="text-muted-foreground mt-1 max-w-[16rem] truncate text-xs xl:hidden" title={r.errorMessage ?? undefined}>{`${r.errorCode}${r.errorMessage ? ` · ${r.errorMessage}` : ''}`}</div>}
                        </TableCell>
                        <TableCell className="text-muted-foreground hidden lg:table-cell">{r.sentAt ? fmtDateTime(r.sentAt) : '—'}</TableCell>
                        <TableCell className="text-muted-foreground hidden sm:table-cell">{r.deliveredAt ? fmtDateTime(r.deliveredAt) : r.failedAt ? fmtDateTime(r.failedAt) : '—'}</TableCell>
                        <TableCell className="text-muted-foreground hidden max-w-xs truncate text-xs xl:table-cell" title={r.errorMessage ?? undefined}>
                          {r.errorCode ? `${r.errorCode}${r.errorMessage ? ` · ${r.errorMessage}` : ''}` : ''}
                        </TableCell>
                        <TableCell className="text-muted-foreground/60">
                          <ChevronRightIcon className="size-4" aria-hidden />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {recipients.data.total > pageSize && <Pagination page={page} pageSize={pageSize} total={recipients.data.total} onPageChange={setPage} onPageSizeChange={setPageSize} />}
            </>
          )}
        </section>
      )}

      <ConfirmDialog
        open={confirm === 'send' || confirm === 'send-override'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm === 'send-override' ? 'Send during quiet hours?' : 'Send this message now?'}
        description={confirm === 'send-override' ? 'It is quiet hours at the job site. Send anyway only for urgent safety notices.' : 'The audience is resolved as of now and texts go out immediately.'}
        confirmLabel={confirm === 'send-override' ? 'Send anyway' : 'Send now'}
        destructive={confirm === 'send-override'}
        loading={sendNow.isPending}
        onConfirm={() => doSend(confirm === 'send-override')}
      />
      <ConfirmDialog
        open={confirm === 'cancel'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={m.status === 'scheduled' ? 'Cancel this scheduled message?' : 'Discard this draft?'}
        description="Nothing is sent. The message stays in history as cancelled."
        confirmLabel={m.status === 'scheduled' ? 'Cancel send' : 'Discard'}
        destructive
        loading={cancel.isPending}
        onConfirm={async () => {
          try {
            await cancel.mutateAsync(m.id);
            toast.success('Cancelled');
            setConfirm(null);
            navigate(`/jobs/${job.id}/history?view=upcoming`);
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
    </div>
  );
}

function Fact({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <dt className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm tabular-nums">{children}</dd>
    </div>
  );
}

function LangPill({ on, onClick, title, children }: { on: boolean; onClick: () => void; title?: string; children: ReactNode }) {
  return (
    <button type="button" role="tab" aria-selected={on} onClick={onClick} title={title} className={cn('border px-2 py-0.5 text-xs transition-colors', on ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:border-foreground/30')}>
      {children}
    </button>
  );
}

function Stat({ segment, label, value, total, bad }: { segment: (typeof DELIVERY_SEGMENTS)[number]['key']; label: string; value: number; total: number; bad?: boolean }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-start gap-2">
      <DeliverySwatch segment={segment} className="mt-1.5" />
      <div className="min-w-0">
        <dd className={cn('text-lg leading-none font-semibold tracking-tight tabular-nums', bad && value > 0 && 'text-destructive')}>
          {fmtNumber(value)}
          {value > 0 && total > 0 && <span className="text-muted-foreground ml-1 text-xs font-normal">{fmtPercent(pct)}</span>}
        </dd>
        <dt className="text-muted-foreground mt-1 text-xs">{label}</dt>
      </div>
    </div>
  );
}
